import { useCallback, useEffect, useState } from 'react';

/*
 * Installable PWA hint (ADR-0018: manifest + shell-only service worker now, Web Push later).
 * Chromium fires `beforeinstallprompt`; we hold the event and re-fire it from our own banner.
 * iOS Safari has no event, so the hint explains "Share -> Add to Home Screen" there.
 */

export const INSTALL_HINT_DISMISSED_KEY = 'servicetim.portal.installHint.dismissed';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type InstallState =
  | { kind: 'hidden' }
  | { kind: 'prompt'; install: () => Promise<boolean> }
  | { kind: 'ios' };

const storage = {
  get(): boolean {
    try {
      return window.localStorage.getItem(INSTALL_HINT_DISMISSED_KEY) === '1';
    } catch {
      return false;
    }
  },
  set(): void {
    try {
      window.localStorage.setItem(INSTALL_HINT_DISMISSED_KEY, '1');
    } catch {
      // Private mode or storage disabled: the hint simply shows again next time.
    }
  },
};

export function isStandalone(nav: Navigator = navigator): boolean {
  return (
    (typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(display-mode: standalone)').matches) ||
    (nav as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function isIosSafari(ua: string = navigator.userAgent): boolean {
  return /iphone|ipad|ipod/i.test(ua) && /safari/i.test(ua) && !/crios|fxios|edgios/i.test(ua);
}

export interface InstallPrompt {
  state: InstallState;
  dismissed: boolean;
  dismiss: () => void;
}

export function useInstallPrompt(): InstallPrompt {
  const [dismissed, setDismissed] = useState<boolean>(() => storage.get());
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState<boolean>(() => isStandalone());

  useEffect(() => {
    const onPrompt = (event: Event) => {
      event.preventDefault();
      setDeferred(event as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const dismiss = useCallback(() => {
    storage.set();
    setDismissed(true);
  }, []);

  const install = useCallback(async () => {
    if (!deferred) return false;
    await deferred.prompt();
    const choice = await deferred.userChoice;
    setDeferred(null);
    return choice.outcome === 'accepted';
  }, [deferred]);

  let state: InstallState = { kind: 'hidden' };
  if (!installed && !dismissed) {
    if (deferred) state = { kind: 'prompt', install };
    else if (isIosSafari()) state = { kind: 'ios' };
  }
  return { state, dismissed, dismiss };
}
